import { SingleDatePicker } from 'react-dates';
import moment from 'moment';
import React from 'react';

import Form from 'react-bootstrap/Form';
import { Field } from 'react-final-form';
import { InputContainer } from '../components/Input';
import { isValidPhoneNumber } from 'react-phone-number-input';

const adaptBootstrap = (Component, config) => ({
  input,
  showValidState = false,
  meta: { valid },
  ...rest
}) => {
  const validState = showValidState
    ? { isValid: !!valid, isInvalid: !valid }
    : {};
  return <Component {...input} {...rest} {...config} {...validState} />;
};

export const FormSelect = ({
  input,
  meta: { valid },
  showValidState = false,
  options,
  ...rest
}) => {
  const validState = showValidState
    ? { isValid: !!valid, isInvalid: !valid }
    : {};

  return (
    <Form.Control as="select" {...rest} {...input} {...validState}>
      <option value="" key="default-option">
        Choose An Option
      </option>
      {options.map(({ value, text }) => (
        <option value={value} key={value}>
          {text}
        </option>
      ))}
    </Form.Control>
  );
};

export const MultipleCheckBoxes = ({ name, options }) =>
  options.map(option => (
    <Form.Group key={option.value}>
      <Field
        name={name}
        component={FormCheckbox}
        id={option.value}
        type="checkbox"
        value={option.value}
        label={option.text}
        inline
      />
    </Form.Group>
  ));

/* single date picker */
export const FormSingleDatePicker = ({
  input,
  meta,
  ...rest
}) => {
  const date = input.value ? moment(input.value) : null;

  return (
    <SingleDatePicker
      {...rest}
      date={date}
      onDateChange={input.onChange}
      focused={meta.active}
      onFocusChange={() => {
        const focusTrigger = !meta.active ? 'onFocus' : 'onBlur';
        input[focusTrigger]();
      }}
    />
  );
};

/* adapted bootstrap inputs */
export const FormInput = adaptBootstrap(Form.Control, { as: 'input' });
export const FormTextArea = adaptBootstrap(Form.Control, { as: 'textarea' });
export const FormCheckbox = adaptBootstrap(Form.Check, { type: 'checkbox' });
export const FormRadio = adaptBootstrap(Form.Check, { type: 'radio' });

/**
 * InputContainerFormField - a component that wraps a form field InputContainer behavior
 *
 * Any extra prop (autoComplete, inputMode, className, placeholder, …) is forwarded to the
 * <Field> and lands on the DOM <input> — that's how autofill hints reach it.
 *
 * `children` are deliberately dropped. Callers used to nest their own <Field> inside this
 * component and it was never rendered, so every attribute on it — validation included — had
 * no effect. Pass those props to this component instead.
 *
 * @export
 * @param {*} { name, type, label, touched, errors = {}, validate, ...inputProps }
 * @return {*}
 */
export function InputContainerFormField({
  name,
  type,
  label,
  touched,
  errors = {},
  validate = required,
  // eslint-disable-next-line no-unused-vars -- swallowed on purpose, see above
  children,
  ...inputProps
}) {
  return (
    <InputContainer error={touched[name] && errors[name]}>
      <Field
        name={name}
        type={type}
        component={FormInput}
        validate={validate}
        showValidState
        id={name}
        {...inputProps}
      />
      {/* htmlFor pairs with the id above, so the field has a real label for both
          screen readers and autofill's field classification */}
      <label htmlFor={name}>{label}</label>
    </InputContainer>
  )
}

/**
 * Error - a component used to display validation errors
 * @param {string} name - form field that we're keeping an eye on.
 *
 */
export const Error = ({ name }) => {
  return (
    <Field name={name} subscription={{ touched: true, error: true }}>
      {({ meta: { touched, error } }) => {
        return touched && error ? (
          <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        ) : null;
      }}
    </Field>
  );
};

/**
 * Error - a component used to display validation errors
 * @param {string} name - form field that we're keeping an eye on.
 *
 */
export const Success = ({ name, message = 'Looks great!' }) => {
  return (
    <Field name={name} subscription={{ touched: true, error: true }}>
      {({ meta: { touched, error } }) => {
        return touched && !error ? (
          <Form.Control.Feedback type="valid">{message}</Form.Control.Feedback>
        ) : null;
      }}
    </Field>
  );
};

/**
 * Condition - a component that renders its children when a certain condition is met.
 * @props {string} when - name of form component we're checking against
 * @props {string} [optional] is - what we expect it to equal
 * @props {function} [optional] isFunc - a function used to determine if the condition is met if `is` is not given
 */

export const Condition = ({
  when,
  is,
  children,
  isTruthy,
  isFalsey,
  isNot,
  isFunc
}) => {
  let displayChildren = () => false;
  if (isTruthy) {
    displayChildren = value => !!value;
  } else if (isFalsey) {
    displayChildren = value => !value;
  } else if (is) {
    displayChildren = value => value === is;
  } else if (isNot) {
    displayChildren = value => value !== isNot;
  } else if (isFunc) {
    displayChildren = isFunc;
  }

  return (
    <Field name={when} subscription={{ value: true }}>
      {({ input: { value } }) => (displayChildren(value) ? children : null)}
    </Field>
  );
};

// validation functions
export const required = value => (value ? undefined : 'Required');
export const matchesOtherValue = (value, values, key) => values[key] === value;
export const mergeRequired = arrayOfValidations => (value, values) => {
  for (let i = 0; i <= arrayOfValidations.length; i++) {
    if (!arrayOfValidations[i](value, values)) {
      return false;
    }
  }
  return true;
};
export const isNumber = value =>
  Number.isFinite(Number.parseFloat(value)) ? undefined : 'Not a number!';

// format functions
export const toNumber = value => (+value ? +value : 0);

export const isEmail = value => {  
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(value) ? undefined : 'Invalid email address';
}

export const isEmailBoolean = value => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(value)
}

export const signInDisabled = (state, termsAccepted, working, formModeSignUp) => (
  working ||
  !isEmailBoolean(state.email.value) ||
  state.password.value.length < 6 ||
  (formModeSignUp && !termsAccepted) ||
  (formModeSignUp && !isValidPhoneNumber(state.phone.value)) ||
  (formModeSignUp && !state.nameFirst.value.length) ||
  (formModeSignUp && !state.nameLast.value.length)
)

export const validateEmail = value => {
  // Regular expression for validating email
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Check if the value matches the email format
  if (!value) {
    return 'Email is required'; // Return an error message if the value is empty
  } else if (!regex.test(value)) {
    return 'Invalid email address'; // Return an error message if the value is not a valid email
  }

  return undefined; // Return undefined if the value is a valid email
}
